// Admin dashboard — Bulk WhatsApp modal (composer / queue / summary).
// Pure structural extraction from AdminDashboard.jsx (~245 lines JSX).
// Zero behaviour/UI change: every data-testid, class name, message
// template, WhatsApp URL, and event handler wiring is preserved. State
// still lives in AdminDashboard — the modal is a controlled component
// driven entirely by the parent's props.
//
// If you find yourself changing the delivery message copy or the
// business-only reminder here, STOP and check whether the change was
// really requested — this component's only purpose is to render.

import React from "react";
import {
  MessageCircle, ArrowLeft, ArrowRight, X, AlertTriangle,
  CheckCircle2, ShieldCheck,
} from "lucide-react";
import { buildBulkWhatsAppMessage, hasValidMobile } from "./utils";

export default function BulkWhatsAppModal({
  state,            // null | "composer" | "queue" | "summary"
  queue,            // frozen [{ ...row }] snapshot
  update,
  setUpdate,
  idx,              // current queue index
  previewIdx,
  setPreviewIdx,
  sent,
  skipped,
  close,
  startQueue,
  markSent,
  markSkipped,
  openChat,
}) {
  if (!state) return null;

  return (
    <div
      data-testid="bulk-whatsapp-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={() => state === "queue" ? null : close()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Composer */}
        {state === "composer" && (() => {
          const previewRow = queue[previewIdx] || queue[0];
          return (
            <>
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-[#25D366]/10 p-2.5 text-[#128C7E] flex-shrink-0">
                  <MessageCircle size={18} strokeWidth={1.9} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="overline text-[10px]">Bulk WhatsApp</p>
                  <h3 className="font-serif text-xl text-ink mt-1 leading-tight">
                    Send a delivery update to {queue.length} customer{queue.length === 1 ? "" : "s"}
                  </h3>
                  <p className="text-[11px] text-muted2 mt-1 leading-relaxed">
                    You'll press Send on WhatsApp for each customer one at a time. Nothing is sent automatically.
                  </p>
                </div>
              </div>

              <div className="mt-5">
                <label className="overline text-[10px]" htmlFor="bulk-wa-update">Delivery Update</label>
                <input
                  id="bulk-wa-update"
                  data-testid="bulk-wa-update-input"
                  type="text"
                  value={update}
                  onChange={(e) => setUpdate(e.target.value)}
                  placeholder="Monday · Tomorrow · Tomorrow or Tuesday"
                  className="luxury-input mt-1 w-full"
                  autoFocus
                />
                <p className="text-[11px] text-muted2 mt-1.5 leading-relaxed">
                  Just the update — the rest of the message is generated for you. Words like "Tomorrow" or "Today" are inserted naturally; anything else becomes "on {"{"}your text{"}"}".
                </p>
              </div>

              <div className="mt-4 rounded-2xl border border-line bg-ivory p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="overline text-[10px]">Preview</p>
                  {queue.length > 1 && (
                    <div className="flex items-center gap-1 text-[11px] text-muted2">
                      <button
                        type="button"
                        data-testid="bulk-wa-preview-prev"
                        onClick={() => setPreviewIdx((i) => Math.max(0, i - 1))}
                        disabled={previewIdx === 0}
                        className="p-1 rounded-full hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ArrowLeft size={13} strokeWidth={1.7} />
                      </button>
                      <span data-testid="bulk-wa-preview-counter">{previewIdx + 1} / {queue.length}</span>
                      <button
                        type="button"
                        data-testid="bulk-wa-preview-next"
                        onClick={() => setPreviewIdx((i) => Math.min(queue.length - 1, i + 1))}
                        disabled={previewIdx >= queue.length - 1}
                        className="p-1 rounded-full hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ArrowRight size={13} strokeWidth={1.7} />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-muted2 mt-0.5">
                  Invoice <span className="text-ink font-medium">{previewRow?.invoice_number}</span> · {previewRow?.customer_name || "—"}
                </p>
                <pre
                  data-testid="bulk-wa-preview-text"
                  className="mt-2 whitespace-pre-wrap font-sans text-[12.5px] text-ink leading-relaxed"
                >
                  {buildBulkWhatsAppMessage(previewRow, update || "…")}
                </pre>
              </div>

              <div className="mt-3 rounded-xl bg-[#25D366]/[0.08] border border-[#128C7E]/25 px-3 py-2 flex items-start gap-2">
                <ShieldCheck size={13} strokeWidth={1.9} className="text-[#128C7E] flex-shrink-0 mt-0.5" />
                <p className="text-[11px] leading-relaxed text-ink">
                  <span className="font-medium">Business only.</span>{" "}
                  <span className="text-muted2">
                    Send from the Tanish Textiles WhatsApp Business account — never personal WhatsApp.
                    If your device shows the app chooser, always tap <span className="text-ink font-medium">WhatsApp Business</span>.
                  </span>
                </p>
              </div>

              <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <button
                  data-testid="bulk-wa-cancel"
                  onClick={close}
                  className="btn-outline-maroon !px-4 !py-2 !text-xs"
                >
                  Cancel
                </button>
                <button
                  data-testid="bulk-wa-start"
                  onClick={startQueue}
                  disabled={!update.trim()}
                  className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowRight size={13} strokeWidth={1.7} /> Start Sending
                </button>
              </div>
            </>
          );
        })()}

        {/* Queue */}
        {state === "queue" && (() => {
          const cur = queue[idx];
          if (!cur) return null;
          const validMob = hasValidMobile(cur);
          const displayMobile = validMob
            ? `+91 ${String(cur.mobile).replace(/\D/g, "").slice(-10).replace(/^(\d{5})/, "$1 ")}`
            : null;
          return (
            <>
              <div className="flex items-center justify-between gap-3">
                <p className="overline text-[10px]">Bulk WhatsApp · {idx + 1} of {queue.length}</p>
                <button
                  data-testid="bulk-wa-close"
                  onClick={close}
                  className="text-muted2 hover:text-maroon"
                  title="Close (progress will be lost)"
                >
                  <X size={16} strokeWidth={1.7} />
                </button>
              </div>
              <div className="h-1.5 rounded-full bg-ivory mt-3 overflow-hidden">
                <div
                  className="h-full bg-maroon transition-[width] duration-300"
                  style={{ width: `${((idx) / queue.length) * 100}%` }}
                />
              </div>

              <div className="mt-5 rounded-2xl border border-line p-4">
                <p className="overline text-[10px]">Invoice</p>
                <p data-testid="bulk-wa-invoice" className="font-serif text-2xl text-ink mt-0.5 leading-tight">{cur.invoice_number}</p>
                <p data-testid="bulk-wa-customer" className="text-sm text-ink mt-2">{cur.customer_name || "—"}</p>
                {validMob ? (
                  <p data-testid="bulk-wa-mobile" className="text-[13px] text-muted2 mt-0.5 tabular-nums">{displayMobile}</p>
                ) : (
                  <p
                    data-testid="bulk-wa-mobile-missing"
                    className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-[#8A2A2A] bg-[#F1DCDC]/60 rounded-full px-2.5 py-1 border border-[#8A2A2A]/20"
                  >
                    <AlertTriangle size={12} strokeWidth={1.8} /> Mobile number unavailable
                  </p>
                )}
              </div>

              {validMob && (
                <button
                  data-testid="bulk-wa-open-btn"
                  onClick={openChat}
                  className="mt-4 w-full rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-medium text-sm px-4 py-3 inline-flex items-center justify-center gap-2 transition-colors"
                >
                  <MessageCircle size={15} strokeWidth={2} /> Open in WhatsApp Business
                </button>
              )}

              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  data-testid="bulk-wa-skip"
                  onClick={markSkipped}
                  className="rounded-full border border-line text-muted2 hover:text-maroon hover:border-maroon/40 text-xs font-medium px-4 py-2.5 inline-flex items-center justify-center gap-1.5 transition-colors"
                >
                  Skip
                </button>
                <button
                  data-testid="bulk-wa-sent"
                  onClick={markSent}
                  disabled={!validMob}
                  className="rounded-full bg-maroon hover:bg-maroon-hover text-ivory text-xs font-medium px-4 py-2.5 inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <CheckCircle2 size={13} strokeWidth={1.9} /> Sent — Next
                </button>
              </div>
              <p className="text-[11px] text-muted2 mt-3 leading-relaxed text-center">
                Tap "Open in WhatsApp", press Send inside WhatsApp, then return here and tap "Sent — Next".
              </p>
            </>
          );
        })()}

        {/* Summary */}
        {state === "summary" && (
          <>
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-[#EDF6EA] p-2.5 text-[#2D4A22] flex-shrink-0">
                <CheckCircle2 size={18} strokeWidth={1.8} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="overline text-[10px]">Bulk WhatsApp Complete</p>
                <h3 className="font-serif text-xl text-ink mt-1">
                  Queue finished
                </h3>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-line p-4">
                <p className="overline text-[10px] text-[#2D4A22]">Sent</p>
                <p data-testid="bulk-wa-summary-sent" className="font-serif text-3xl text-ink mt-1">{sent.length}</p>
              </div>
              <div className="rounded-2xl border border-line p-4">
                <p className="overline text-[10px] text-[#8A2A2A]">Skipped</p>
                <p data-testid="bulk-wa-summary-skipped" className="font-serif text-3xl text-ink mt-1">{skipped.length}</p>
              </div>
            </div>

            {skipped.length > 0 && (
              <p className="text-[11px] text-muted2 mt-3 leading-relaxed">
                Skipped: <span className="text-ink font-medium">{skipped.join(", ")}</span>
              </p>
            )}

            <p className="text-[11px] text-muted2 mt-3 leading-relaxed">
              "Sent" reflects the messages you completed through the queue — WhatsApp delivery or read receipts are not tracked here.
            </p>

            <div className="mt-5 flex justify-end">
              <button
                data-testid="bulk-wa-done"
                onClick={close}
                className="btn-maroon !px-4 !py-2 !text-xs"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
